// services/zeno.service.js
import assistantClient, { handleApiError } from '../api/assistantClient';
import { streamChat, createStreamController } from '../utils/sseClient';

const extractMessageFromContentBlocks = (contentBlocks) => {
  if (!Array.isArray(contentBlocks)) {
    return '';
  }

  const textBlock = contentBlocks.find((block) => block?.type === 'text' && typeof block?.data?.text === 'string' && block.data.text.trim());
  return textBlock?.data?.text?.trim() || '';
};

const normalizeContentBlocks = (contentBlocks) => {
  if (!Array.isArray(contentBlocks) || contentBlocks.length === 0) {
    return null;
  }

  return contentBlocks.map((block, index) => ({
    ...block,
    order: index + 1,
  }));
};

const buildZenoPayload = ({ userId, message, chatHistoryId, context, workflowContext, useMemory, contentBlocks }) => {
  const normalizedContentBlocks = normalizeContentBlocks(contentBlocks);
  const normalizedMessage = (typeof message === 'string' ? message.trim() : '') || extractMessageFromContentBlocks(normalizedContentBlocks);

  const payload = {
    userId,
    useMemory,
  };

  if (normalizedMessage) {
    payload.message = normalizedMessage;
  }

  if (normalizedContentBlocks) {
    payload.contentBlocks = normalizedContentBlocks;
  }

  if (chatHistoryId) {
    payload.chatHistoryId = chatHistoryId;
  }
  if (context) {
    payload.context = context;
  }
  if (workflowContext) {
    payload.workflowContext = workflowContext;
  }

  return { payload, normalizedMessage, normalizedContentBlocks };
};

/**
 * Servicio para interactuar con Zeno Bot
 * Zeno es el asistente especializado de Zenit AI para workflows
 */
class ZenoService {
  /**
   * Obtener información de Zeno y verificar disponibilidad
   */
  async getZenoInfo() {
    try {
      const response = await assistantClient.get('/zeno');
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Verificar salud de Zeno
   */
  async getZenoHealth() {
    try {
      const response = await assistantClient.get('/zeno/health');
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Obtener capacidades de Zeno
   */
  async getZenoCapabilities() {
    try {
      const response = await assistantClient.get('/zeno/capabilities');
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Chat con Zeno - NUEVO ENDPOINT
   * POST /accounts/{account_id}/zeno/chat
   * Body: { "userId": "user_id", "message": "Tu mensaje", "useMemory": true }
   * Response: { "chatHistoryId": "id", "response": "respuesta", ... }
   */
  async chatWithZeno({ 
    accountId,
    userId,
    message, 
    contentBlocks,
    chatHistoryId = null,
    context = null,
    workflowContext = null, 
    useMemory = true 
  }) {
    try {
      // Validar parámetros requeridos
      if (!accountId) {
        return { 
          success: false, 
          error: { message: 'Account ID is required' }
        };
      }

      if (!userId) {
        return { 
          success: false, 
          error: { message: 'User ID is required' }
        };
      }

      const { payload, normalizedMessage, normalizedContentBlocks } = buildZenoPayload({
        userId,
        message,
        contentBlocks,
        chatHistoryId,
        context,
        workflowContext,
        useMemory,
      });

      if (!normalizedMessage && !normalizedContentBlocks?.length) {
        return { 
          success: false, 
          error: { message: 'Message or content blocks are required' }
        };
      }

      console.log('🤖 Sending message to Zeno:', { accountId, userId, payload });

      // Usar NUEVO endpoint con account_id
      const response = await assistantClient.post(`/accounts/${accountId}/zeno/chat`, payload);
      console.log('🤖 Zeno response:', response.data);
      
      return { 
        success: true, 
        data: {
          messageId: response.data.messageId,
          chatHistoryId: response.data.chatHistoryId,
          response: response.data.response,
          workflowSuggestion: response.data.workflowSuggestion,
          nextActions: response.data.nextActions,
          metadata: response.data.metadata,
          contentBudget: response?.data?.metadata?.content_budget || response?.data?.metadata?.contentBudget || null,
        }
      };
    } catch (error) {
      console.error('Error chatting with Zeno:', error);
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Chat con Zeno - STREAMING VERSION
   * POST /accounts/{account_id}/zeno/chat/stream
   * Usa Server-Sent Events (SSE) para recibir la respuesta en chunks
   * 
   * @param {Object} params - Parámetros del mensaje
   * @param {string} params.userId - User ID (requerido)
   * @param {Object} callbacks - Callbacks para manejar el stream
   * @param {Function} callbacks.onContent - Llamado con cada chunk de texto
   * @param {Function} callbacks.onMetadata - Llamado con metadata final
   * @param {Function} callbacks.onDone - Llamado cuando termina el stream
   * @param {Function} callbacks.onError - Llamado si hay error
   * @returns {Object} { controller } - Controller para cancelar el stream
   */
  chatWithZenoStream({ 
    accountId,
    userId,
    message, 
    contentBlocks,
    chatHistoryId = null,
    context = null,
    workflowContext = null, 
    useMemory = true 
  }, callbacks = {}) {
    const { onContent, onMetadata, onDone, onError } = callbacks;

    // Validar parámetros requeridos
    const { payload, normalizedMessage, normalizedContentBlocks } = buildZenoPayload({
      userId,
      message,
      contentBlocks,
      chatHistoryId,
      context,
      workflowContext,
      useMemory,
    });

    if (!accountId || !userId || (!normalizedMessage && !normalizedContentBlocks?.length)) {
      if (onError) {
        onError(new Error('Account ID, User ID y message or content blocks are required'));
      }
      return { controller: null };
    }

    console.log('🤖 Sending streaming message to Zeno:', { accountId, userId, payload });

    // Crear controller para poder cancelar
    const controller = createStreamController(120000); // 2 min timeout

    // Iniciar streaming
    streamChat(`/accounts/${accountId}/zeno/chat/stream`, {
      body: payload,
      signal: controller.signal,
      onContent,
      onMetadata: (meta) => {
        if (!onMetadata) return;
        const contentBudget = meta?.content_budget || meta?.contentBudget || null;
        onMetadata({ ...meta, contentBudget });
      },
      onDone,
      onError,
    });

    return { controller };
  }

  /**
   * Obtener sugerencia de workflow de Zeno
   */
  async suggestWorkflow({ userId, workflowDescription, context, accountId }) {
    try {
      const payload = {
        user_id: userId,
        workflow_description: workflowDescription,
      };

      if (context) {
        payload.context = context;
      }

      if (accountId) {
        payload.account_id = accountId;
      }

      const response = await assistantClient.post('/zeno/workflows/suggest', payload);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Obtener un chat history específico con sus mensajes
   * GET /chat/histories/{chat_history_id} + GET /chat/histories/{chat_history_id}/messages
   */
  async getChatHistory(chatHistoryId) {
    try {
      if (!chatHistoryId) {
        return { 
          success: false, 
          error: { message: 'Chat History ID is required' }
        };
      }

      // Primero obtener metadata del chat.
      // El backend exige `chat_history_id` también como query (además del path),
      // si no responde 422 "Field required".
      const chatResponse = await assistantClient.get(`/chat/histories/${chatHistoryId}`, {
        params: { chat_history_id: chatHistoryId },
      });
      
      // El endpoint principal NO incluye mensajes, debemos obtenerlos por separado
      let messages = [];
      try {
        const messagesResponse = await assistantClient.get(`/chat/histories/${chatHistoryId}/messages`);
        // La respuesta tiene estructura: { messages: [...], chat_history_id, total, limit, offset }
        messages = messagesResponse.data.messages || [];
      } catch (messagesError) {
        console.warn('Could not fetch messages for chat history:', messagesError);
        // Si falla, devolver sin mensajes pero con metadata del chat
        messages = [];
      }
      
      // Combinar metadata con mensajes
      const combinedData = {
        ...chatResponse.data,
        messages: messages
      };
      
      return { success: true, data: combinedData };
    } catch (error) {
      console.error('Error getting chat history:', error);
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Listar todos los chats con Zeno para una cuenta
   * GET /accounts/{account_id}/chat/histories?assistant_id=00000000-0000-0000-0000-0000000000e0
   */
  async listZenoChats(accountId, { limit = 50, offset = 0, search = null } = {}) {
    try {
      if (!accountId) {
        return {
          success: false,
          error: { message: 'Account ID is required' }
        };
      }

      // ID específico de Zeno según tu documentación
      const zenoAssistantId = '00000000-0000-0000-0000-0000000000e0';

      const trimmedSearch = typeof search === 'string' ? search.trim() : '';
      const response = await assistantClient.get(`/accounts/${accountId}/chat/histories`, {
        params: {
          assistant_id: zenoAssistantId,
          limit,
          offset,
          ...(trimmedSearch ? { search: trimmedSearch } : {}),
        }
      });

      // La respuesta tiene la estructura: { chatHistories: Array, total: number, limit: number, offset: number }
      return { 
        success: true, 
        data: response.data.chatHistories || [], // Extraer solo el array de chatHistories
        total: response.data.total || 0,
        limit: response.data.limit || limit,
        offset: response.data.offset || offset
      };
    } catch (error) {
      console.error('Error listing Zeno chats:', error);
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Cargar mensajes de un chat history específico
   * Útil para continuar una conversación existente
   */
  async loadChatMessages(chatHistoryId) {
    try {
      const result = await this.getChatHistory(chatHistoryId);
      
      if (result.success) {
        const messages = result.data.messages || [];
        
        // Transformar mensajes a formato compatible con el hook
        const transformedMessages = messages.map(msg => {
          let content = '';
          
          // Extraer contenido de texto de contentBlocks si existe. Solo se toman
          // los bloques de texto: bloques estructurados (image/file/options) los
          // renderiza el componente a partir de contentBlocks, y serializarlos a
          // JSON aquí ensuciaba el texto del mensaje (p.ej. un bloque `options`
          // aparecía como JSON crudo al recargar).
          const textBlocks = (msg.contentBlocks && Array.isArray(msg.contentBlocks))
            ? msg.contentBlocks.filter(block => block?.type === 'text')
            : [];
          if (textBlocks.length > 0) {
            content = textBlocks.map(block => {
              if (typeof block.data === 'string') return block.data;
              if (block.data?.text) return block.data.text;
              return block.content || block.text || '';
            }).join('\n').trim();
          } else {
            // Fallback a campos tradicionales
            content = msg.content || msg.message || msg.text || '';
          }
          
          return {
            id: msg.id || msg.messageId,
            role: msg.role === 'human' ? 'user' : (msg.role === 'assistant' ? 'assistant' : msg.role),
            message: content,
            content: content,
            contentBlocks: msg.contentBlocks, // Mantener original para el renderizado
            createdAt: msg.createdAt || msg.timestamp,
            metadata: msg.metadata
          };
        });
        
        return { 
          success: true, 
          data: {
            ...result.data,
            messages: transformedMessages
          }
        };
      }
      
      return result;
    } catch (error) {
      console.error('Error loading chat messages:', error);
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Helper para crear contexto de plataforma web
   */
  createWebContext() {
    return {
      platform: "web",
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent
    };
  }

  /**
   * Helper para crear contexto de workflow
   */
  createWorkflowContext(intent, integration = null, additionalData = {}) {
    const workflowContext = {
      intent,
      ...additionalData
    };

    if (integration) {
      workflowContext.integration = integration;
    }

    return workflowContext;
  }

  /**
   * Método conveniencia para iniciar chat nuevo
   */
  async startNewChat(accountId, userId, message, workflowIntent = null) {
    const context = this.createWebContext();
    
    const params = {
      accountId,
      userId,
      message,
      context,
      useMemory: true
    };

    // Si hay intención de workflow, agregar contexto
    if (workflowIntent) {
      params.workflowContext = this.createWorkflowContext(workflowIntent);
    }

    return this.chatWithZeno(params);
  }

  /**
   * Método conveniencia para continuar chat existente
   */
  async continueChat(accountId, userId, message, chatHistoryId, workflowContext = null) {
    const context = this.createWebContext();
    
    return this.chatWithZeno({
      accountId,
      userId,
      message,
      chatHistoryId,
      context,
      workflowContext,
      useMemory: true
    });
  }
}

export default new ZenoService();
